import { PDFDocument, StandardFonts, PageSizes } from "pdf-lib";
import {
  HARI_PENTING,
  type HariPenting,
  getAllHariPentingForYear,
} from "@/data/hari-penting";
import { HARI_CONTENT } from "@/lib/hari-penting/content";
import { MONTH_NAMES_ID, DAY_NAMES_ID, dayOfWeek } from "@/lib/calendar/dates";
import { getHolidaysForYear } from "@/data/holidays";
import {
  RED, BLUE, GRAY, TEXT, GREEN,
  type BaseOpts, embedLogo, drawBrandHeader, wrapText, drawFooter,
} from "./pdf-shared";

export interface HariPdfOpts extends BaseOpts {
  /** If empty, export all. Otherwise filter HARI_PENTING by these slugs. */
  slugs?: string[];
  /** Reference year used when computing day-of-week. Default current year. */
  year?: number;
  /** Optional date range filter (ISO yyyy-mm-dd, inclusive). Overrides slugs filter when set. */
  dateRange?: { from: string; to: string };
  /** Optional period label override on cover. */
  periodLabel?: string;
}

/** Multi-entry PDF: cover + one section per hari. */
export async function generateHariPDF(opts: HariPdfOpts = {}): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const logo = await embedLogo(doc, opts.logo, opts.useDefaultLogo);
  const year = opts.year ?? new Date().getFullYear();

  // Build the actual list of (hari, year) pairs to render
  type Entry = { h: HariPenting; y: number; iso: string };
  let entries: Entry[] = [];
  if (opts.dateRange) {
    const { from, to } = opts.dateRange;
    const fy = parseInt(from.slice(0, 4), 10);
    const ty = parseInt(to.slice(0, 4), 10);
    for (let yy = fy; yy <= ty; yy++) {
      for (const h of HARI_PENTING) {
        const [mm, dd] = h.monthDay.split("-");
        const iso = `${yy}-${mm}-${dd}`;
        if (iso >= from && iso <= to) entries.push({ h, y: yy, iso });
      }
    }
    entries.sort((a, b) => a.iso.localeCompare(b.iso));
  } else {
    const base: HariPenting[] = (opts.slugs && opts.slugs.length > 0
      ? (opts.slugs.map((s) => HARI_PENTING.find((h) => h.slug === s)).filter(Boolean) as HariPenting[])
      : HARI_PENTING).slice().sort((a, b) => a.monthDay.localeCompare(b.monthDay));
    entries = base.map((h) => {
      const [mm, dd] = h.monthDay.split("-");
      return { h, y: year, iso: `${year}-${mm}-${dd}` };
    });
  }
  const list: HariPenting[] = entries.map((e) => e.h);

  const holidays = getHolidaysForYear(year);

  // Cover page
  const cover = doc.addPage(PageSizes.A4);
  const Wc = cover.getWidth(), Hc = cover.getHeight();
  const brandBottom = drawBrandHeader(cover, logo, font, bold, 30, Hc - 22, "Hari Penting Indonesia");
  const periodTitle = opts.periodLabel
    ? `Rekap Hari Penting: ${opts.periodLabel}`
    : `Daftar Hari Penting ${year}`;
  cover.drawText(periodTitle, { x: 30, y: brandBottom - 24, size: 20, font: bold, color: TEXT });
  cover.drawText(
    `${entries.length} entri terpilih`,
    { x: 30, y: brandBottom - 40, size: 10, font, color: GRAY },
  );

  // Index list on cover
  let cy = brandBottom - 70;
  cover.drawText("Daftar isi", { x: 30, y: cy, size: 12, font: bold, color: TEXT }); cy -= 16;
  const colW = (Wc - 60 - 16) / 2;
  let col = 0;
  let yLeft = cy, yRight = cy;
  for (const e of entries) {
    const [mm, dd] = e.h.monthDay.split("-").map(Number);
    const isHol = holidays.some((x) => x.date === e.iso && x.type === "libur");
    const text = `${dd} ${MONTH_NAMES_ID[mm - 1]} ${e.y} - ${e.h.name}`;
    const lines = wrapText(text, font, 9, colW - 10);
    const x = col === 0 ? 30 : 30 + colW + 16;
    const yRef = col === 0 ? yLeft : yRight;
    if (yRef < 60) { col = col === 0 ? 1 : 0; continue; }
    let ly = yRef;
    for (const ln of lines) {
      cover.drawText(ln, { x, y: ly, size: 9, font, color: isHol ? RED : TEXT });
      ly -= 12;
    }
    if (col === 0) yLeft = ly - 2; else yRight = ly - 2;
    col = col === 0 ? 1 : 0;
  }
  drawFooter(cover, font, opts.siteUrl ?? "https://hari.co.id/hari/");

  // One page per hari (or 2 per page if short). Keep simple: 1 per page for PDF clarity.
  for (const e of entries) {
    const h = e.h;
    const yy = e.y;
    const [mm, dd] = h.monthDay.split("-").map(Number);
    const content = HARI_CONTENT[h.slug];
    const page = doc.addPage(PageSizes.A4);
    const W = page.getWidth(), H = page.getHeight();
    const brandB2 = drawBrandHeader(page, logo, font, bold, 30, H - 22, "Hari Penting");
    page.drawText(h.name, { x: 30, y: brandB2 - 22, size: 20, font: bold, color: TEXT });
    const dow = dayOfWeek(yy, mm, dd);
    const yearHols = yy === year ? holidays : getHolidaysForYear(yy);
    const isHol = yearHols.some((x) => x.date === e.iso && x.type === "libur");
    page.drawText(`${dd} ${MONTH_NAMES_ID[mm - 1]} (${DAY_NAMES_ID[dow]} ${yy})`, {
      x: 30, y: brandB2 - 40, size: 11, font, color: isHol ? RED : GRAY,
    });
    page.drawText(h.scope === "nasional" ? "Peringatan Nasional Indonesia" : "Peringatan Internasional", {
      x: 30, y: brandB2 - 55, size: 9, font, color: h.scope === "nasional" ? BLUE : GREEN,
    });
    if (isHol) {
      page.drawText("Status: Libur Nasional", { x: 30, y: brandB2 - 70, size: 9, font: bold, color: RED });
    }

    let y = brandB2 - (isHol ? 95 : 80);
    const block = (title: string, body: string) => {
      if (y < 90) return;
      page.drawText(title, { x: 30, y, size: 11, font: bold, color: TEXT }); y -= 14;
      const lines = wrapText(body, font, 9, W - 60);
      for (const ln of lines) {
        if (y < 80) return;
        page.drawText(ln, { x: 30, y, size: 9, font, color: TEXT });
        y -= 12;
      }
      y -= 8;
    };

    if (content?.tagline) block("Ringkasan", content.tagline);
    if (content?.quickAnswer) block("Jawaban Cepat", content.quickAnswer);
    if (content?.history) block("Sejarah", content.history);
    if (content?.meaning) block("Makna", content.meaning);
    if (content?.figure) {
      block(
        "Tokoh Kunci",
        `${content.figure.name} (${content.figure.role})${content.figure.bio ? ". " + content.figure.bio : ""}`,
      );
    }
    if (content?.traditions && content.traditions.length > 0) {
      block("Cara Memperingati", content.traditions.map((t) => "- " + t).join("\n"));
    }
    if ((!content?.history && !content?.meaning && !content?.tagline)) {
      block(
        "Tentang",
        `${h.name} diperingati setiap tanggal ${dd} ${MONTH_NAMES_ID[mm - 1]} sebagai peringatan ${h.scope === "nasional" ? "nasional Indonesia" : "internasional"}.`,
      );
    }

    drawFooter(page, font, opts.siteUrl ?? `https://hari.co.id/hari/${h.slug}/`);
  }

  // Touch import to avoid unused warning when no slugs filter -> still consume helper.
  void getAllHariPentingForYear;
  return doc.save();
}